/**
 * JWT 토큰 서비스
 *
 * 쿠키 기반 JWT 토큰의 조회 / 저장 / 삭제 / 상태 확인을 일괄 관리한다.
 */

import { cookieUtils } from './cookie';
import { AuthTokens } from '@/types/auth_types';

export const tokenService = {
  // ─── 조회 ────────────────────────────────────────────────

  /** 액세스 토큰 조회 */
  getAccessToken(): string | null {
    return cookieUtils.get('accessToken');
  },

  /** 리프레시 토큰 조회 */
  getRefreshToken(): string | null {
    return cookieUtils.get('refreshToken');
  },

  /** 액세스 · 리프레시 토큰 동시 조회 (하나라도 없으면 null) */
  getTokens(): AuthTokens | null {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) return null;

    return { accessToken, refreshToken };
  },

  // ─── 저장 ────────────────────────────────────────────────

  /** 액세스 토큰 저장 */
  setAccessToken(token: string): void {
    cookieUtils.set('accessToken', token);
  },

  /** 리프레시 토큰 저장 */
  setRefreshToken(token: string): void {
    cookieUtils.set('refreshToken', token);
  },

  /** 액세스 · 리프레시 토큰 동시 저장 */
  setTokens(tokens: AuthTokens): void {
    this.setAccessToken(tokens.accessToken);
    this.setRefreshToken(tokens.refreshToken);
  },

  // ─── 삭제 ────────────────────────────────────────────────

  /** 액세스 토큰 삭제 */
  removeAccessToken(): void {
    cookieUtils.remove('accessToken');
  },

  /** 리프레시 토큰 삭제 */
  removeRefreshToken(): void {
    cookieUtils.remove('refreshToken');
  },

  /** 액세스 · 리프레시 토큰 동시 삭제 */
  removeTokens(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
  },

  /** 모든 쿠키 삭제 (토큰 포함) */
  clearAllCookies(): void {
    cookieUtils.clearAll();
  },

  // ─── 상태 확인 ───────────────────────────────────────────

  /** 액세스 토큰 존재 여부 */
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  },

  /** 리프레시 토큰 존재 여부 */
  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  },

  /** 액세스 · 리프레시 토큰 모두 존재 여부 */
  hasValidTokens(): boolean {
    return this.hasAccessToken() && this.hasRefreshToken();
  },

  /** 로그인 상태 여부 (액세스 토큰 존재 기준) */
  isAuthenticated(): boolean {
    return this.hasAccessToken();
  },
} as const;
